/* eslint-disable arrow-body-style */
/* eslint-disable camelcase */
/* eslint-disable no-unused-vars */
// TODO cache?
import {
  SkillRecord,
  StandardRecord,
} from '@components/Component/Lesson/LessonContainer/Types';
import {
  CHALKTALK,
  DISTRICT,
  MY_QUESTIONS,
} from '@components/LearnosityLessonReview/LessonReviewSetup/ItemBankToggleButtonGroup';
import shuffle from './utils/shuffle';
import stringToSeed from './utils/stringToSeed';
import rateLimiter from './utils/rateLimiter';

interface Question {
  id: string;
  reference: string;
  organisation_id?: number;
  standards?: string[];
  difficulty: string | null;
  depthOfKnowledge?: string;
  bloom: string | null;
  skill?: SkillRecord;
  depth_of_knowledge?: string;
}

interface LearnosityConfig {
  regions: any;
  title: string;
  subtitle?: string;
  questions_api_init_options: any;
  configuration: any;
  navigation?: {
    auto_save?: {
      changed_responses_only?: boolean;
      save_interval_duration?: number;
    };
    warning_on_change?: boolean;
    skip_submit_confirmation?: boolean;
  };
  time?: {
    countdown?: number;
    countdown_option?: boolean;
    limit_type?: 'soft' | 'hard';
    max_time?: number;
    start_time?: {
      type: 'soft' | 'hard';
      value?: string;
    };
    warning_time?: number;
  };
  labelBundle?: any;
}

export interface LearnosityMetadata {
  standards_association: Record<string, string[]>;
  difficulty: Record<string, string | null>;
  bloom: Record<string, string | null>;
  depthOfKnowledge: Record<string, string | null>;
  skill: Record<string, SkillRecord | undefined>;
}

export type LearnosityApiService = 'items' | 'assess' | 'author' | 'reports';

interface LearnosityItemsSignatureRequest {
  name: string;
  user_id: string;
  session_id: string;
  activity_id: string;
  rendering_type: 'assess' | 'inline';
  type: 'local_practice' | 'submit_practice' | 'feedback';
  state: 'review' | 'initial' | 'resume' | 'preview';
  config: LearnosityConfig;
  session_metadata?: LearnosityMetadata;
  items?: Question[];
  activity_template_id?: string;
}

interface LearnosityAuthoringSignatureRequest {
  mode: 'item_list' | 'item_edit';
  config: any;
  reference?: string | boolean;
}

type LearnositySignatureRequest =
  | LearnosityItemsSignatureRequest
  | LearnosityAuthoringSignatureRequest;

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface LearnositySignature {}
interface LearnosityUser {
  id: string;
  district_id: string;
}

const tagsToHide = [
  'ItemLogicGUID',
  'ItemLogicID',
  'ItemLogicLocalID',
  'VendorID',
  'MaxPoints',
  'Status',
  'ESTranslationVendorID',
  // 'AB GUID',
  'Item Type',
  'Generated',
  'Generation ID',
  'Author',
  'Author ID',
  'Chalktalk Subject ID',
  'Chalktalk Subject',
];

// TODO Language support
// We add "NonExistantSkillName" if the skills is empty
// So we dont end up pulling ALL questions
const getAllFilters = (skills) => {
  const filters = [{ type: 'Language', name: 'EN' }];

  if (skills.length === 0) {
    filters.push({ type: 'Skill', name: 'NonExistantSkillName' });
  }

  return filters;
};

export const convertQuestionToCamelCase = (question: Question): Question => {
  const newQuestion = { ...question };
  newQuestion.depthOfKnowledge = question.depthOfKnowledge || question.depth_of_knowledge;
  delete newQuestion.depth_of_knowledge;
  return newQuestion;
};

// https://reference.learnosity.com/author-api/initialization/config.item_list
const createAuthoringSignatureRequest: (
  user: LearnosityUser,
  skills: Array<SkillRecord>,
) => LearnosityAuthoringSignatureRequest = (user, skills) => ({
  organisation_id: Number(
    process.env.REACT_APP_LEARNOSITY_ADAPTIVE_ORGANISATION_ID || 653,
  ), // chalktalk-prod
  mode: 'item_list',
  config: {
    global: {
      hide_tags: tagsToHide.map((tag) => ({ type: tag })),
      ab_standards: {
        enable: true,
        aligned_tag_type: 'AB GUID',
        tag_standard_hierarchy: false,
        edit_standards: false,
        filter: false,
      },
    },
    item_list: {
      filter: {
        restricted: {
          status: ['published'],
          tags: {
            all: getAllFilters(skills),
            either: skills.map((skill) => ({ type: 'Skill', name: skill.slug })),
            none: [
              { type: 'Item Type', name: 'ItemOnly' },
              { type: 'Generated', name: '1' },
            ],
          },
        },
      },
      item: {
        enable_selection: true,
        status: false,
        // url: "?questions[':reference']",
        title: {
          show_reference: true,
          show: true,
        },
      },
      limit: 10, // items per page
      toolbar: {
        add: false,
        search: {
          status: false, // disable searching by status
          controls: ['content'],
          tags: {
            show: true, // allow search by tags
          },
        },
      },
    },
    item_edit: {
      item: {
        actions: {
          show: true,
        },
        answers: true,
        columns: false,
        duplicate: {
          show: false,
        },
        save: {
          persist: false,
          show: false,
        },
        mode: {
          default: 'preview',
          show: false,
        },
      },
      widget: {
        edit: false,
        delete: false,
      },
    },
  },
  user: {
    id: 'demos-site',
    firstname: 'Demos',
    lastname: 'User',
    email: 'demos@learnosity.com',
  },
});

const createReviewQuestionSignatureRequest: (
  reference: string,
) => LearnosityAuthoringSignatureRequest = (reference) => ({
  organisation_id: Number(
    process.env.REACT_APP_LEARNOSITY_ADAPTIVE_ORGANISATION_ID || 654,
  ), // chalktalk-dev
  mode: 'item_edit',
  reference,
  config: {
    global: {
      hide_tags: tagsToHide.map((tag) => ({ type: tag })),
    },
    dependencies: {
      questions_api: {
        preload: true,
        init_options: {
          show_distractor_rationale: true,
        },
      },
    },
    item_edit: {
      item: {
        actions: {
          show: true,
        },
        answers: true,
        columns: false,
        duplicate: {
          show: false,
        },
        save: {
          persist: false,
          show: false,
        },
        mode: {
          default: 'preview',
          show: false,
        },
        back: false,
      },
      widget: {
        edit: false,
        delete: false,
      },
    },
  },
  user: {
    id: 'demos-site',
    firstname: 'Demos',
    lastname: 'User',
    email: 'demos@learnosity.com',
  },
});

const createEditQuestionSignatureRequest: (
  reference: string,
  user: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  },
) => LearnosityAuthoringSignatureRequest = (reference, user) => ({
  organisation_id: Number(
    process.env.REACT_APP_LEARNOSITY_ADAPTIVE_ORGANISATION_ID || 654,
  ), // chalktalk-dev
  mode: 'item_edit',
  reference,
  config: {
    dependencies: {
      questions_api: {
        preload: true,
        init_options: {
          show_distractor_rationale: true,
        },
      },
    },
  },
  user: {
    id: `${user.id}`,
    firstname: user.first_name,
    lastname: user.last_name,
    email: user.email,
  },
});

const createEditActivitySignatureRequest: (
  activity_template_id: string,
  user: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  },
) => LearnosityAuthoringSignatureRequest = (activity_template_id, user) => ({
  organisation_id: Number(
    process.env.REACT_APP_LEARNOSITY_ADAPTIVE_ORGANISATION_ID || 654,
  ), // chalktalk-dev
  mode: 'activity_edit',
  reference: activity_template_id,
  config: {
    activity_edit: {
      back: false,
      annotations: {
        enabled: true,
      },
    },
  },
  user: {
    id: `${user.id}`,
    firstname: user.first_name,
    lastname: user.last_name,
    email: user.email,
  },
});

// configuration options for the rich text editor in CoAuthor
const coauthor_rich_text_editor_toolbar = [
  {
    items: ['Styles'],
    name: 'style',
  },
  {
    items: [
      'Bold',
      'Italic',
      'Underline',
      '-',
      'TextColor',
      '-',
      'LrnUnderlinedIndicator',
      '-',
      'RemoveFormat',
      'FontSize',
    ],
    name: 'basicstyles',
  },
  {
    items: ['NumberedList', 'BulletedList', '-', 'Indent', 'Outdent'],
    name: 'list',
  },
  {
    items: ['JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock'],
    name: 'justify',
  },
  {
    items: ['Link', 'Unlink'],
    name: 'link',
  },
  {
    items: ['Image', 'LrnMath', 'Table', 'Blockquote', 'SpecialChar'],
    name: 'insert',
  },
  {
    items: ['LrnSimpleFeature'],
    name: 'simplefeature',
  },
];

// Sets allowed question types for CoAuthor. We are removing non-autoscorable types for now...
// reference: https://help.learnosity.com/hc/en-us/articles/360000755038-Widget-Template-References-for-Question-Editor-Customization-Navigation
const coauthor_question_type_groups = [
  {
    reference: 'mcq',
    template_references: [
      '9e8149bd-e4d8-4dd6-a751-1a113a4b9163', // Multiple choice – standard
      '908de244-5c71-4c09-b094-7fb49554f2f9', // Multiple choice – multiple response
      '3egs0b24-5gs8-49fc-fds9-4a450sdg31ca', // True or false
      '33d53a22-1a59-4a03-9671-7f5104edd62e', // Multiple choice – block layout
    ],
  },
  {
    reference: 'match',
    template_references: [
      '1fa22aac-1f88-47f7-941b-3c77759549e6', // Match list
      'ef2648de-f826-4674-b17d-71e71889d8e6', // Classification
      '0c7f68f7-2be6-4ae0-8492-0ab78c8d0010', // Order list
      '14b7af71-e146-4747-aae5-ca71618a5038', // Choice matrix - standard
      'f109c77c-390e-4595-93b8-2dbd547251b6', // Choice matrix - inline
      '9de82e14-802c-4bea-a635-bf9ad0b622fb', // Choice matrix - labels
    ],
  },
  {
    reference: 'cloze',
    template_references: [
      '2fbba51b-e35e-441f-83c7-2662e2e81fa6', // Cloze with drop down
      '457fe101-0667-4a35-b193-b849653acb52', // Cloze with text
      '51a8c1e7-f34f-4faf-b211-da458e891fcb', // Cloze with drag & drop
      '6e77b403-8f0c-43af-b464-9450e1ac70dc', // Label image with drag & drop
      'f8364191-ada5-4806-83d4-8a36b8fad4b0', // Label image with drop-down
      '35a850a7-9d3f-4e1c-880a-a340767942b6', // Label image with text
    ],
  },
  {
    reference: 'highlight',
    template_references: [
      '47f0db98-79e8-4273-946f-90faf4a2ae49', // Token highlight
      'f0416e39-72fc-4d40-8d6e-c6f97e621fd4', // Hotspot
    ],
  },
  {
    reference: 'math',
    template_references: [
      'ef0ab0ba-235f-459d-9380-a4a106280c9a', // Math
      '7ebe931e-06b8-4fc8-95d2-c7ec4fbf1239', // Multi-step math
      '76504b1a-8f37-4ab8-bcbf-875c966c72af', // Label image with math
      '57b0690f-4e99-4bbc-9c8f-aa3074baa495', // Cloze math
      '11c9093a-dfd3-4852-b1ee-d5e5c3f032df', // Cloze math with image
      'ec8f2f4e-c048-4347-906a-32b0c44c0515', // Math question generator
      'f0416e39-72fc-4d40-8d6e-c6f97e621fd5', // Gridded
    ],
  },
  {
    reference: 'graph',
    template_references: [
      'b7dc8b69-c12a-4c4f-ac17-181ac6e0da26', // Graphing
      'f325a114-bb81-4e7f-86e0-1147416a78a8', // Graphing in the 1st quadrant
      'f5c683be-b5e0-421d-a593-f6a59312aba2', // Number line with drag & drop
      '02a2d2fe-c028-42d0-b139-e58a60780928', // Number line with plot
    ],
  },
  {
    reference: 'chart',
    template_references: [
      '24f76ba0-24af-4be4-aacf-c31c6efd33e3', // Bar chart
      '8c28ecfd-ab0b-43de-bfac-e572eeb06851', // Line chart
      'cb9cb540-05b2-44e0-bef3-1b31727c1c37', // Histogram
      '2f93c2f1-f7d9-423a-94c9-5df5aabdddc9', // Dot plot
      '1f20bb6e-8685-4eaa-9c70-ef80b811e483', // Line plot
    ],
  },
  {
    reference: 'writespeak',
    template_references: [
      '4fe6c726-419c-4aff-b189-4a9756a0ffda', // Short text
    ],
  },
  {
    reference: 'chemistry',
    template_references: [
      'e484fac0-4022-4b58-b86f-4a537002f449', // Chemistry formula
      '36e1da6f-5966-4912-a547-75bffdd77950', // Cloze chemistry
      '4d2a04cb-f8a9-4678-b8c6-0dd4bce25fbc', // Cloze chemistry with image
    ],
  },
  {
    reference: 'other',
    template_references: [
      'b30bc5dc-b616-4913-a8b4-b5dd9c43bafd', // Shading
      '8ff43fa9-804b-42e7-9b3e-3cdd7e3a882b', // Image annotation upload
    ],
  },
];

const createCoAuthorItemRequest: (userId: string) => LearnosityAuthoringSignatureRequest =
  (userId) => ({
    organisation_id: Number(
      process.env.REACT_APP_LEARNOSITY_ADAPTIVE_ORGANISATION_ID || 654, // default chalktalk-dev
    ),
    mode: 'item_edit',
    reference: '---', // dont instantiate an item upon load
    config: {
      widget_config: {
        require_validation: true,
      },
      item_edit: {
        mode: {
          default: 'preview',
        },
        widget: {
          edit: true,
          delete: false,
        },
        settings: {
          show: false,
        },
        item: {
          actions: {
            show: true,
          },
          details: {
            source: {
              show: false,
            },
          },
          answers: true,
          back: false,
          columns: false,
          duplicate: {
            show: false,
          },
          mode: {
            default: 'preview',
            show: true,
          },
          tags: {
            show: false,
          },
          save: {
            persist: false, // prevent saving directly to itembank!!
            show: true,
          },
          shared_passage: false, // we dont want users picking passages within the item
        },
      },
      dependencies: {
        questions_api: {
          init_options: {
            show_distractor_rationale: true,
          },
        },
        question_editor_api: {
          init_options: {
            ui: {
              source_button: false,
            },
            rich_text_editor: {
              toolbar_settings: {
                ltr_toolbar: coauthor_rich_text_editor_toolbar,
              },
            },
            question_type_groups: coauthor_question_type_groups,
          },
        },
      },
    },
    user: {
      id: `${userId}`,
    },
  });

const createPassageRequest: (
  user_id: string,
  mode: string,
  reference?: string,
) => LearnosityAuthoringSignatureRequest = (userId, mode, reference) => ({
  organisation_id: Number(
    process.env.REACT_APP_LEARNOSITY_ADAPTIVE_ORGANISATION_ID || 654, // default chalktalk-dev
  ),
  reference,
  mode: 'item_edit',
  config: {
    item_edit: {
      item: {
        actions: {
          show: true,
        },
        answers: true,
        columns: false,
        duplicate: {
          show: false,
        },
        save: {
          persist: false,
          show: false,
        },
        mode: {
          default: mode,
          show: false,
        },
      },
      widget: {
        edit: false,
        delete: false,
      },
    },
  },
  user: {
    id: `${userId}`,
  },
});

const createPassageItemsListRequest: (
  passageReference: string,
  userId: string,
) => LearnosityAuthoringSignatureRequest = (passageReference, userId) => ({
  organisation_id: Number(
    process.env.REACT_APP_LEARNOSITY_ADAPTIVE_ORGANISATION_ID || 654, // default chalktalk-dev
  ),
  mode: 'item_list',
  config: {
    dependencies: {
      questions_api: {
        init_options: {
          show_distractor_rationale: true,
        },
      },
    },
    item_list: {
      toolbar: {
        add: false,
      },
      filter: {
        restricted: {
          status: ['published'],
          tags: {
            all: [
              { type: 'Passage Reference', name: passageReference },
              { type: 'Author ID', name: userId },
            ],
          },
        },
      },
    },
    item_edit: {
      item: {
        reference: {
          show: false,
          edit: false,
        },
        duplicate: {
          show: false,
        },
        mode: {
          default: 'preview',
          show: false,
        },
        save: false,
      },
      widget: {
        delete: false,
        edit: false,
      },
    },
  },
  user: {
    id: `${userId}`,
  },
});

const createItemsSignatureRequest: (
  name: string,
  user_id: string,
  session_id: string,
  activity_id: string,
  rendering_type: 'assess' | 'inline',
  type: 'local_practice' | 'submit_practice' | 'feedback',
  state: 'review' | 'initial' | 'resume' | 'preview',
  config: LearnosityConfig,
  items?: Question[],
  activity_template_id?: string,
) => LearnosityItemsSignatureRequest = (
  name,
  user_id,
  session_id,
  activity_id,
  rendering_type,
  type,
  state,
  config,
  items,
  activity_template_id,
) => {
  // derive metadata from questions and build metadata association
  const session_metadata = items?.reduce(
    (metadata, item) => {
      /* eslint-disable no-param-reassign */
      metadata.standards_association[item.id] = item.standards ?? [];
      metadata.difficulty[item.id] = item.difficulty;
      metadata.bloom[item.id] = item.bloom;
      metadata.depthOfKnowledge[item.id] =
        item.depthOfKnowledge || item.depth_of_knowledge || null;
      if (item.skill) {
        metadata.skill[item.id] = item.skill;
      }
      return metadata;
    },
    {
      bloom: {},
      depthOfKnowledge: {},
      difficulty: {},
      standards_association: {},
      skill: {},
    } as LearnosityMetadata,
  );

  return {
    activity_id,
    config,
    name,
    rendering_type,
    session_id,
    state,
    type,
    user_id,
    ...(session_metadata && { session_metadata }),
    ...(items?.length && { items }),
    ...(activity_template_id && { activity_template_id }),
  };
};

const getSignature: (
  baseUrl: string,
  environment: string,
  signatureRequest: LearnositySignatureRequest,
  api: LearnosityApiService,
) => Promise<LearnositySignature> = async (baseUrl, environment, signatureRequest, api) =>
  fetch(`${baseUrl}/signature?environment=${environment}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ api, request: signatureRequest }),
  }).then((res) => res.json());

// TODO error handling
const getSessionQuestions: (
  baseUrl: string,
  environment: string,
  sessionId: string,
) => Promise<Question[]> = async (baseUrl, environment, sessionId) => {
  const questions = await fetch(
    `${baseUrl}/sessionquestions?id=${sessionId}&environment=${environment}`,
  )
    .then((response) => response.json())
    .then((response) => {
      if (response.errorMessage) {
        throw response;
      }
      return response;
    });
  return questions;
};
type getQuestionsArgs = {
  baseUrl: string;
  environment: string;
  standards: string[];
  limit: number;
  lessonCode: string;
  offset?: number;
  publication?: string;
  user: string;
  language: string;
};
const getQuestions = async ({
  baseUrl,
  environment,
  lessonCode,
  limit,
  offset = 0,
  publication = 'CCSS',
  standards,
  user,
  language = 'en',
}: getQuestionsArgs): Promise<Question[]> => {
  const questionsResponse = await rateLimiter(() =>
    fetch(
      `${baseUrl}/v2/questions?standard=${JSON.stringify(
        standards,
      )}&limit=${limit}&offset=${offset}&environment=${environment}&seed=${lessonCode}&publication=${publication}&lang=${language}`,
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        return response.json();
      })
      .then((response) => {
        if (response.errorMessage) {
          throw response;
        }
        const userSeed = stringToSeed(user);
        return shuffle(response, userSeed);
      }),
  );
  return questionsResponse || [];
};

export {
  createItemsSignatureRequest,
  createAuthoringSignatureRequest,
  createReviewQuestionSignatureRequest,
  createCoAuthorItemRequest,
  createPassageRequest,
  createPassageItemsListRequest,
  getSignature,
  getQuestions,
  getSessionQuestions,
  createEditQuestionSignatureRequest,
  createEditActivitySignatureRequest,
  Question,
  LearnosityConfig,
  LearnositySignatureRequest,
  LearnosityAuthoringSignatureRequest,
  LearnosityItemsSignatureRequest,
  LearnositySignature,
};
